import type { components } from '@social-studio/api-client';
import { api } from '../../lib/api';
import { fieldErrors, problemSlug, type Problem } from '../knowledge/problems';

export type SearchResult = components['schemas']['SearchResultDto'];
export type SearchEntityType = SearchResult['entity_type'];
export type SearchPage = { readonly data: SearchResult[]; readonly next_cursor: string | null };

/** Display order of the result groups (and of the type chips). */
export const SEARCH_TYPES: readonly SearchEntityType[] = ['content_item', 'campaign', 'media_asset', 'entry', 'knowledge_item'];

/** Server limits (spec 110 §5): a shorter query is never sent. */
export const SEARCH_MIN_LENGTH = 2;
export const SEARCH_MAX_LENGTH = 100;

export interface SearchParams {
  readonly q: string;
  /** Empty = every type the member may read. */
  readonly types: readonly SearchEntityType[];
  readonly includeArchived: boolean;
}

/** `invalid`: the server refused the query itself (retrying the same text is pointless). */
export class SearchLoadError extends Error {
  constructor(
    readonly status: number,
    readonly reason: 'invalid' | 'too_many_terms' | 'failed',
  ) {
    super(`search answered ${status}`);
  }
}

/** One page of `GET /v1/search`; `cursor` continues the same query. */
export async function fetchSearchPage(params: SearchParams, cursor: string | undefined): Promise<SearchPage> {
  const { data, error, response } = await api.GET('/v1/search', {
    params: {
      query: {
        q: params.q,
        types: params.types.length > 0 ? [...params.types] : undefined,
        include_archived: params.includeArchived || undefined,
        cursor,
      },
    },
  });
  if (response.ok && data) return data;
  const problem = error as Problem | undefined;
  if (response.status === 422 && problemSlug(problem) === 'validation-failed') {
    throw new SearchLoadError(422, fieldErrors(problem).q === 'too_many_terms' ? 'too_many_terms' : 'invalid');
  }
  throw new SearchLoadError(response.status, 'failed');
}
